import React, { useState, useCallback, useEffect, useMemo, useRef } from "react";
import {
  Form,
  Input,
  Select,
  Table,
  Tag,
  Tooltip,
  Empty,
  DatePicker,
  Popover,
  Button,
  Typography,
} from "antd";
import { SearchOutlined, CloseCircleOutlined, ArrowLeftOutlined, GiftOutlined } from "@ant-design/icons";
import { useParams, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { useTheme } from "../../contexts/ThemeContext";
import { useRequestValidation } from "../../shared/hooks/useRequestValidation";
import { gamificationApi } from "../../shared/api/gamificationApi";
import ReceiptCard from "../ReceiptCard";
import { ValidationContent } from "../ValidationContent";
import type { TransferOption } from "../RequestsPanel/types";
import "./styles.css";

const REJECTION_REASONS = {
  NO_TRANSFER: "No llego la transferencia",
  FAKE_RECEIPT: "Comprobante Falso",
  OTHER: "Otro motivo",
} as const;


export const RequestValidationPage: React.FC = () => {
  const { requestId } = useParams<{ requestId: string }>();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const isDark = theme === "dark";

  // Theme class helper
  const tc = (base: string) => `${base} ${base}--${isDark ? "dark" : "light"}`;

  const [form] = Form.useForm();
  const [unregisteredForm] = Form.useForm();
  const [rejectForm] = Form.useForm();

  const [activeTab, setActiveTab] = useState<"registered" | "unregistered">("registered");
  const [selectedTransferId, setSelectedTransferId] = useState<number | null>(null);
  const [notifyUser, setNotifyUser] = useState(true);
  const [processDeposit, setProcessDeposit] = useState(true);
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [showCustomReason, setShowCustomReason] = useState(false);

  const {
    fullRequestData,
    isLoadingDetails,
    extractedOCRData,
    paginatedTransfers,
    isLoadingTransfers,
    processingRequestIds,
    setSearchParams,
    transferPagination,
    setTransferPagination,
    processMutation,
    registerUnregisteredMutation,
    rejectMutation,
    resolvePanelId,
  } = useRequestValidation(requestId);

  const isProcessing =
    (requestId ? processingRequestIds.includes(requestId) : false) ||
    processMutation.isPending ||
    registerUnregisteredMutation.isPending;

  // Bonus preview
  const [bonusPreview, setBonusPreview] = useState<{ estimatedBonus: number; rulesBonus?: number; breakdown: Array<{ ruleName: string; bonusAmount: number; bonusType: string }>; userLevel?: { level: number; name: string; multiplier: number } } | null>(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const previewTimerRef = useRef<ReturnType<typeof setTimeout>>();

  const registeredAmount = Form.useWatch("amount", form);
  const unregisteredAmount = Form.useWatch("amount", unregisteredForm);
  const watchedAmount = activeTab === "registered" ? registeredAmount : unregisteredAmount;

  useEffect(() => {
    if (previewTimerRef.current) clearTimeout(previewTimerRef.current);

    const numAmount = parseFloat(watchedAmount);
    const userId = fullRequestData?.user?.id;
    if (!userId || !numAmount || numAmount <= 0) {
      setBonusPreview(null);
      setIsLoadingPreview(false);
      return;
    }

    setIsLoadingPreview(true);
    previewTimerRef.current = setTimeout(async () => {
      try {
        const res = await gamificationApi.getBonusPreview(userId, numAmount);
        const data = res?.data ?? res;
        setBonusPreview(data);
      } catch {
        setBonusPreview(null);
      } finally {
        setIsLoadingPreview(false);
      }
    }, 500);

    return () => { if (previewTimerRef.current) clearTimeout(previewTimerRef.current); };
  }, [watchedAmount, fullRequestData?.user?.id, activeTab]);

  useEffect(() => {
    if (processMutation.isSuccess || registerUnregisteredMutation.isSuccess || rejectMutation.isSuccess) {
      navigate("/user-panel/requests");
    }
  }, [processMutation.isSuccess, registerUnregisteredMutation.isSuccess, rejectMutation.isSuccess, navigate]);

  useEffect(() => {
    if (activeTab === "unregistered" && extractedOCRData) {
      setTimeout(() => {
        const vals: any = {
          amount: extractedOCRData.amount,
          coelsaId: extractedOCRData.coelsaId,
          sourcePlatform: extractedOCRData.sourcePlatform,
          bonus: 0,
        };
        if (extractedOCRData.dateTime) vals.dateTime = dayjs(extractedOCRData.dateTime);
        unregisteredForm.setFieldsValue(vals);
      }, 100);
    }
  }, [activeTab, extractedOCRData, unregisteredForm]);

  const record = useMemo(() => {
    if (!fullRequestData) return null;
    return {
      id: requestId,
      type: "LOAD" as const,
      status: fullRequestData.status,
      requestNumber: fullRequestData.requestNumber,
      user: fullRequestData.user,
      receipt: fullRequestData.receipt,
      linkedTransfer: fullRequestData.linkedTransfer,
      operation: fullRequestData.operation,
      requestData: fullRequestData.requestData,
      rejectionReason: fullRequestData.rejectionReason,
    };
  }, [fullRequestData, requestId]);

  const details = useMemo(() => {
    if (!fullRequestData) return null;
    return {
      ...fullRequestData,
      status: fullRequestData.status || record?.status,
      requestData: {
        retryHistory: fullRequestData.requestData?.retryHistory || [],
        processingSteps: (fullRequestData.requestData?.processingSteps || []).map((step: any) => ({
          ...step,
          timestamp: typeof step.timestamp === "string" ? step.timestamp : new Date(step.timestamp).toISOString(),
          metadata: {
            ...(step.metadata || {}),
            detectedPlatform: step.metadata?.detectedPlatform || "unknown",
            extractedFields: step.metadata?.extractedFields || {},
            comparedTransfers: step.metadata?.comparedTransfers || [],
          },
        })),
      },
    };
  }, [fullRequestData, record?.status]);

  const enrichedRecord = useMemo(() => {
    if (!record) return null;
    return {
      ...record,
      status: fullRequestData?.status || record.status,
      linkedTransfer: fullRequestData?.linkedTransfer || record.linkedTransfer,
    };
  }, [record, fullRequestData]);

  // --- Handlers ---
  const handleBack = useCallback(() => navigate("/user-panel/requests"), [navigate]);

  const handleProcessWithTransfer = useCallback(
    async (values: { transferId: number; amount: number; bonus: number }) => {
      if (!requestId) return;
      const panelId = await resolvePanelId();
      if (!panelId) return;
      await processMutation.mutateAsync({
        requestId, transferId: values.transferId, amount: values.amount,
        bonus: values.bonus, notify: notifyUser, processDeposit, panelId,
      });
    },
    [requestId, processMutation, resolvePanelId, notifyUser, processDeposit]
  );

  const handleProcessUnregistered = useCallback(
    async (values: { dateTime: any; amount: number; coelsaId: string; sourcePlatform: string; bonus: number }) => {
      if (!requestId) return;
      const panelId = await resolvePanelId();
      if (!panelId) return;
      await registerUnregisteredMutation.mutateAsync({
        requestId, dateTime: dayjs(values.dateTime).toISOString(), amount: values.amount,
        coelsaId: values.coelsaId, sourcePlatform: values.sourcePlatform,
        bonus: values.bonus || 0, notify: notifyUser, processDeposit, panelId,
      });
    },
    [requestId, registerUnregisteredMutation, resolvePanelId, notifyUser, processDeposit]
  );

  const handleReject = useCallback(
    async (values: { rejectionReason: string; customReason?: string }) => {
      if (!requestId) return;
      const reason = values.rejectionReason === "Otro motivo" ? values.customReason : values.rejectionReason;
      await rejectMutation.mutateAsync({ requestId, rejectionReason: reason! });
    },
    [requestId, rejectMutation]
  );

  // --- Table columns ---
  const tableColumns = useMemo(() => [
    {
      title: "ID", key: "identifier", width: "38%", ellipsis: true,
      render: (_: any, r: TransferOption) => {
        const tp = r.operationNumber?.startsWith("TP-");
        const id = tp
          ? r.coelsaId || r.cuit || r.cuil || r.operationNumber || "Sin ID"
          : r.coelsaId || r.operationNumber || r.cuit || r.cuil || "Sin ID";
        return (
          <Tooltip title={id}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 2 }}>
              <span style={{ maxWidth: 90, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "inline-block", fontSize: 11 }}>{id}</span>
              {r.score != null && <Tag color={r.score >= 0.9 ? "success" : "default"}>{Math.round(r.score * 100)}%</Tag>}
            </span>
          </Tooltip>
        );
      },
    },
    { title: "$", dataIndex: "amount", key: "amount", width: "22%", render: (v: number) => <span style={{ fontSize: 11 }}>{v?.toFixed(2)}</span> },
    { title: "Fecha", dataIndex: "dateTime", key: "dateTime", width: "25%", render: (d: Date) => <span style={{ fontSize: 11 }}>{new Date(d).toLocaleDateString()}</span> },
    {
      title: "", key: "status", width: "15%",
      render: (_: any, r: TransferOption) => (
        selectedTransferId === r.id && !r.validatedReceiptHash
          ? <Tag color="processing">Sel.</Tag>
          : r.validatedReceiptHash ? <Tag color="#f5222d">Usada</Tag> : null
      ),
    },
  ], [selectedTransferId]);

  // --- Reject popover ---
  const rejectFormContent = (
    <Form form={rejectForm} layout="vertical" onFinish={handleReject} style={{ width: 300 }}>
      <Form.Item name="rejectionReason" rules={[{ required: true, message: "Seleccione un motivo" }]} style={{ marginBottom: 8 }}>
        <Select placeholder="Motivo de rechazo" onChange={(v) => setShowCustomReason(v === REJECTION_REASONS.OTHER)}>
          {Object.entries(REJECTION_REASONS).map(([k, v]) => <Select.Option key={k} value={v}>{v}</Select.Option>)}
        </Select>
      </Form.Item>
      {showCustomReason && (
        <Form.Item name="customReason" rules={[{ required: true, message: "Ingrese su motivo" }]} style={{ marginBottom: 8 }}>
          <Input.TextArea placeholder="Ingrese su motivo" autoSize={{ minRows: 2, maxRows: 4 }} />
        </Form.Item>
      )}
      <div style={{ display: "flex", gap: 8 }}>
        <Button size="small" onClick={() => { setShowRejectForm(false); setShowCustomReason(false); rejectForm.resetFields(); }}>Cancelar</Button>
        <Button danger type="primary" size="small" htmlType="submit" loading={rejectMutation.isPending}>Confirmar</Button>
      </div>
    </Form>
  );

  // --- Loading ---
  if (isLoadingDetails) {
    return (
      <div className="vp-loading">
        <div style={{ textAlign: "center" }}>
          <div className="vp-spinner" />
          <Typography.Text type="secondary">Cargando solicitud...</Typography.Text>
        </div>
      </div>
    );
  }

  if (!fullRequestData || !record) {
    return (
      <div className="vp-loading">
        <div style={{ textAlign: "center" }}>
          <Typography.Text type="secondary" style={{ display: "block", marginBottom: 16 }}>
            No se encontraron datos de la solicitud.
          </Typography.Text>
          <Button type="link" onClick={handleBack} style={{ color: "#ac4949" }}>Volver a solicitudes</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="vp-page">
      {/* ─── HEADER ─── */}
      <div className={tc("vp-header")}>
        <div className="vp-header-left">
          <Button size="small" icon={<ArrowLeftOutlined />} onClick={handleBack} />
          <Typography.Text strong style={{ fontSize: 13 }}>
            {record.user?.username || record.user?.accountName || "Usuario"}
          </Typography.Text>
          {record.requestNumber && (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              #{record.requestNumber}
            </Typography.Text>
          )}
          <Tag
            className="vp-tag"
            color={
              record.status === "REQUIRES_VALIDATION" ? "orange"
              : record.status === "PENDING" ? "blue"
              : record.status === "REJECTED" ? "error"
              : record.status === "COMPLETED" ? "success"
              : "default"
            }
          >
            {record.status}
          </Tag>
        </div>

        <Popover
          content={rejectFormContent}
          title="Rechazar Solicitud"
          trigger="click"
          open={showRejectForm}
          onOpenChange={(v) => { setShowRejectForm(v); if (!v) { setShowCustomReason(false); rejectForm.resetFields(); } }}
          placement="bottomRight"
        >
          <Button
            danger
            size="small"
            icon={<CloseCircleOutlined />}
            disabled={isProcessing || rejectMutation.isPending}
          >
            Rechazar
          </Button>
        </Popover>
      </div>

      {/* ─── MAIN: 3 columns ─── */}
      <div className="vp-main">
        {/* ─── LEFT: Receipt ─── */}
        <div className={tc("vp-col")} style={{ width: "30%", minWidth: 280 }}>
          <div className="vp-scroll" style={{ padding: 12 }}>
            <ReceiptCard loading={false} record={{ ...enrichedRecord!, type: "LOAD" as const }} details={details} />
          </div>
        </div>

        {/* ─── CENTER: Validation Analysis ─── */}
        <div className={`compact-validation-panel ${tc("vp-col")}`} style={{ width: "28%", minWidth: 260 }}>
          <div className="vp-scroll" style={{ padding: "6px 8px" }}>
            <ValidationContent details={details} record={enrichedRecord} loading={false} onLoad={() => {}} />
          </div>
        </div>

        {/* ─── RIGHT: Transfer selection ─── */}
        <div className="vp-col" style={{ flex: 1, minWidth: 0 }}>
          {/* Tab bar + checkboxes */}
          <div className={tc("vp-tabs")}>
            {(["registered", "unregistered"] as const).map((tab) => (
              <div
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`vp-tab ${activeTab === tab ? `vp-tab--active vp-tab--${isDark ? "dark" : "light"}` : ""}`}
              >
                {tab === "registered" ? "Con registro" : "Sin registro"}
              </div>
            ))}
            <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 12, paddingRight: 12 }}>
              <label className="vp-check">
                <input type="checkbox" checked={notifyUser} onChange={(e) => setNotifyUser(e.target.checked)} />
                Notificar
              </label>
              <label className="vp-check">
                <input type="checkbox" checked={processDeposit} onChange={(e) => setProcessDeposit(e.target.checked)} />
                Procesar dep.
              </label>
            </div>
          </div>

          {/* Tab content */}
          {activeTab === "registered" ? (
            <Form form={form} layout="vertical" onFinish={handleProcessWithTransfer} style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
              {/* Filters */}
              <div className={tc("vp-filters")}>
                <Input prefix={<SearchOutlined />} placeholder="ID / CUIT / CUIL" size="small"
                  onChange={(e) => { setSearchParams((p) => ({ ...p, identifier: e.target.value.toLowerCase() })); setTransferPagination((p) => ({ ...p, current: 1 })); }}
                  style={{ flex: 1, minWidth: 100 }} allowClear />
                <Input prefix={<SearchOutlined />} placeholder="Monto" type="number" size="small"
                  onChange={(e) => { setSearchParams((p) => ({ ...p, amount: e.target.value })); setTransferPagination((p) => ({ ...p, current: 1 })); }}
                  style={{ width: 100 }} allowClear />
                <Input prefix={<SearchOutlined />} placeholder="Fecha" size="small"
                  onChange={(e) => { setSearchParams((p) => ({ ...p, dateTime: e.target.value.toLowerCase() })); setTransferPagination((p) => ({ ...p, current: 1 })); }}
                  style={{ width: 110 }} allowClear />
              </div>

              {/* Table + pagination */}
              <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", padding: "0 10px" }}>
                <div style={{ flex: 1, minHeight: 0, overflow: "auto" }}>
                  <Form.Item name="transferId" rules={[{ required: true, message: "Seleccione una transferencia" }]} style={{ marginBottom: 0 }}>
                    <Table
                      dataSource={paginatedTransfers.data}
                      rowKey={(r) => r.id.toString()}
                      className="compact-validation-table"
                      onRow={(r: TransferOption) => ({
                        onClick: () => { if (r.validatedReceiptHash) return; setSelectedTransferId(r.id); form.setFieldsValue({ transferId: r.id, amount: r.amount }); },
                        style: {
                          cursor: r.validatedReceiptHash ? "not-allowed" : "pointer",
                          backgroundColor: selectedTransferId === r.id ? "rgba(172,73,73,0.15)" : undefined,
                          borderLeft: selectedTransferId === r.id ? "3px solid #ac4949" : undefined,
                        },
                      })}
                      columns={tableColumns}
                      pagination={false}
                      loading={isLoadingTransfers}
                      scroll={{ y: "calc(100vh - 280px)" }}
                      size="small"
                      locale={{ emptyText: <Empty description="No hay transferencias" /> }}
                    />
                  </Form.Item>
                </div>
                {/* Pagination */}
                <div className="vp-pagination">
                  <span>{paginatedTransfers.total || 0} transferencias</span>
                  <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                    <Button
                      size="small"
                      disabled={transferPagination.current <= 1}
                      onClick={() => setTransferPagination((p) => ({ ...p, current: p.current - 1 }))}
                      style={{ fontSize: 11, padding: "0 6px", height: 22 }}
                    >
                      Ant.
                    </Button>
                    <span style={{ fontSize: 11, padding: "0 4px" }}>
                      {transferPagination.current} / {Math.ceil((paginatedTransfers.total || 1) / transferPagination.pageSize)}
                    </span>
                    <Button
                      size="small"
                      disabled={transferPagination.current >= Math.ceil((paginatedTransfers.total || 1) / transferPagination.pageSize)}
                      onClick={() => setTransferPagination((p) => ({ ...p, current: p.current + 1 }))}
                      style={{ fontSize: 11, padding: "0 6px", height: 22 }}
                    >
                      Sig.
                    </Button>
                    <Select
                      size="small"
                      value={transferPagination.pageSize}
                      onChange={(v) => setTransferPagination((p) => ({ ...p, pageSize: v, current: 1 }))}
                      style={{ width: 62, fontSize: 11 }}
                      options={[{ value: 10, label: "10" }, { value: 20, label: "20" }, { value: 50, label: "50" }]}
                    />
                  </div>
                </div>
              </div>

              {/* Action bar */}
              <div className={tc("vp-action-bar")}>
                <Form.Item label="Monto" name="amount" rules={[{ required: true, message: "Monto" }]} style={{ marginBottom: 0, flex: 1 }}>
                  <Input type="number" step="0.01" placeholder="0.00" />
                </Form.Item>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Form.Item label="Bono" name="bonus" initialValue={0} rules={[{ required: true, message: "Bono" }]} style={{ marginBottom: 0 }}>
                    <Input type="number" step="0.01" placeholder="0" />
                  </Form.Item>
                  <div style={{ height: 18, marginTop: 2 }}>
                    {isLoadingPreview ? (
                      <span style={{ fontSize: 11, color: isDark ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.25)", fontStyle: "italic" }}>Calculando...</span>
                    ) : bonusPreview ? (
                      bonusPreview.estimatedBonus > 0 ? (
                        <Tooltip
                          color={isDark ? "#1a1a1a" : "#fff"}
                          overlayInnerStyle={{ padding: 0 }}
                          title={
                            <div style={{ padding: "8px 12px", minWidth: 180 }}>
                              <div style={{ fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", opacity: 0.5, marginBottom: 6, color: isDark ? "#aaa" : "#666" }}>
                                Desglose de bono
                              </div>
                              {bonusPreview.userLevel?.multiplier > 1 && (
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "3px 0", fontSize: 12, color: isDark ? "#d9d9d9" : "#333" }}>
                                  <span style={{ opacity: 0.8 }}>Nivel {bonusPreview.userLevel.name} (x{bonusPreview.userLevel.multiplier})</span>
                                  <span style={{ fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>${(bonusPreview.estimatedBonus - (bonusPreview.rulesBonus || 0)).toFixed(2)}</span>
                                </div>
                              )}
                              {bonusPreview.breakdown?.map((b: any, i: number) => (
                                <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "3px 0", fontSize: 12, color: isDark ? "#d9d9d9" : "#333" }}>
                                  <span style={{ opacity: 0.8, maxWidth: 130, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{b.ruleName}</span>
                                  <span style={{ fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>${b.bonusAmount.toFixed(2)}</span>
                                </div>
                              ))}
                              <div style={{ borderTop: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"}`, marginTop: 6, paddingTop: 6, display: "flex", justifyContent: "space-between", fontSize: 12, fontWeight: 700, color: "#52c41a" }}>
                                <span>Total</span>
                                <span>${bonusPreview.estimatedBonus.toFixed(2)}</span>
                              </div>
                            </div>
                          }
                        >
                          <span className="vp-bonus-hint">
                            <GiftOutlined /> Estimado: ${bonusPreview.estimatedBonus.toFixed(2)}
                          </span>
                        </Tooltip>
                      ) : (
                        <span style={{ fontSize: 11, color: isDark ? "rgba(255,255,255,0.25)" : "rgba(0,0,0,0.3)" }}>
                          Estimado: $0.00
                        </span>
                      )
                    ) : null}
                  </div>
                </div>
                <Button type="primary" danger htmlType="submit" loading={isProcessing} disabled={!selectedTransferId || isProcessing} style={{ height: 32 }}>
                  Procesar
                </Button>
              </div>
            </Form>
          ) : (
            /* Unregistered tab */
            <Form form={unregisteredForm} layout="vertical" onFinish={handleProcessUnregistered} initialValues={{ bonus: 0 }} preserve={false} style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
              <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", minHeight: 0, padding: "24px 32px" }}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 24px" }}>
                  <Form.Item label="Fecha y Hora" name="dateTime" rules={[{ required: true, message: "Requerido" }]}>
                    <DatePicker showTime style={{ width: "100%" }} placeholder="Fecha y hora" size="large" />
                  </Form.Item>
                  <Form.Item label="CoelsaId" name="coelsaId" rules={[{ required: true, message: "Requerido" }]}>
                    <Input placeholder="CoelsaId" size="large" />
                  </Form.Item>
                  <Form.Item label="Plataforma" name="sourcePlatform" rules={[{ required: true, message: "Requerido" }]} style={{ gridColumn: "1 / -1" }}>
                    <Select placeholder="Plataforma" size="large">
                      <Select.Option value="mercadopago">Mercado Pago</Select.Option>
                      <Select.Option value="mercadopagoAlt">Mercado Pago Alt</Select.Option>
                      <Select.Option value="uala">Uala</Select.Option>
                      <Select.Option value="brubank">Brubank</Select.Option>
                      <Select.Option value="galicia">Galicia</Select.Option>
                      <Select.Option value="bna">Banco Nacion</Select.Option>
                      <Select.Option value="macro">Macro</Select.Option>
                      <Select.Option value="supervielle">Supervielle</Select.Option>
                      <Select.Option value="astropay">AstroPay</Select.Option>
                      <Select.Option value="astropayAlt">AstroPay Alt</Select.Option>
                      <Select.Option value="naranjax">Naranja X</Select.Option>
                      <Select.Option value="naranjaxAlt">Naranja X Alt</Select.Option>
                      <Select.Option value="personalpay">Personal Pay</Select.Option>
                      <Select.Option value="personalpayAlt">Personal Pay Alt</Select.Option>
                      <Select.Option value="cuentadni">Cuenta DNI</Select.Option>
                      <Select.Option value="ripio">Ripio</Select.Option>
                      <Select.Option value="prex">Prex</Select.Option>
                      <Select.Option value="prexAlt">Prex Alt</Select.Option>
                      <Select.Option value="belo">Belo</Select.Option>
                      <Select.Option value="credicoop">Credicoop</Select.Option>
                      <Select.Option value="rioja">Rioja</Select.Option>
                      <Select.Option value="lemon">Lemon</Select.Option>
                      <Select.Option value="lapampa">La Pampa</Select.Option>
                      <Select.Option value="nbch">NBCH</Select.Option>
                      <Select.Option value="bancosol">Bancosol</Select.Option>
                      <Select.Option value="bancor">Bancor</Select.Option>
                      <Select.Option value="patagonia">Patagonia</Select.Option>
                      <Select.Option value="tierradelfuego">Tierra del Fuego</Select.Option>
                      <Select.Option value="icbc">ICBC</Select.Option>
                    </Select>
                  </Form.Item>
                </div>
              </div>

              {/* Action bar - same layout as "Con registro" */}
              <div className={tc("vp-action-bar")}>
                <Form.Item label="Monto" name="amount" rules={[{ required: true, message: "Monto" }]} style={{ marginBottom: 0, flex: 1 }}>
                  <Input type="number" step="0.01" placeholder="0.00" />
                </Form.Item>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Form.Item label="Bono" name="bonus" initialValue={0} rules={[{ required: true, message: "Bono" }]} style={{ marginBottom: 0 }}>
                    <Input type="number" step="0.01" placeholder="0" />
                  </Form.Item>
                  <div style={{ height: 18, marginTop: 2 }}>
                    {isLoadingPreview ? (
                      <span style={{ fontSize: 11, color: isDark ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.25)", fontStyle: "italic" }}>Calculando...</span>
                    ) : bonusPreview ? (
                      bonusPreview.estimatedBonus > 0 ? (
                        <Tooltip
                          color={isDark ? "#1a1a1a" : "#fff"}
                          overlayInnerStyle={{ padding: 0 }}
                          title={
                            <div style={{ padding: "8px 12px", minWidth: 180 }}>
                              <div style={{ fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", opacity: 0.5, marginBottom: 6, color: isDark ? "#aaa" : "#666" }}>
                                Desglose de bono
                              </div>
                              {bonusPreview.userLevel?.multiplier > 1 && (
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "3px 0", fontSize: 12, color: isDark ? "#d9d9d9" : "#333" }}>
                                  <span style={{ opacity: 0.8 }}>Nivel {bonusPreview.userLevel.name} (x{bonusPreview.userLevel.multiplier})</span>
                                  <span style={{ fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>${(bonusPreview.estimatedBonus - (bonusPreview.rulesBonus || 0)).toFixed(2)}</span>
                                </div>
                              )}
                              {bonusPreview.breakdown?.map((b: any, i: number) => (
                                <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "3px 0", fontSize: 12, color: isDark ? "#d9d9d9" : "#333" }}>
                                  <span style={{ opacity: 0.8, maxWidth: 130, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{b.ruleName}</span>
                                  <span style={{ fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>${b.bonusAmount.toFixed(2)}</span>
                                </div>
                              ))}
                              <div style={{ borderTop: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"}`, marginTop: 6, paddingTop: 6, display: "flex", justifyContent: "space-between", fontSize: 12, fontWeight: 700, color: "#52c41a" }}>
                                <span>Total</span>
                                <span>${bonusPreview.estimatedBonus.toFixed(2)}</span>
                              </div>
                            </div>
                          }
                        >
                          <span className="vp-bonus-hint">
                            <GiftOutlined /> Estimado: ${bonusPreview.estimatedBonus.toFixed(2)}
                          </span>
                        </Tooltip>
                      ) : (
                        <span style={{ fontSize: 11, color: isDark ? "rgba(255,255,255,0.25)" : "rgba(0,0,0,0.3)" }}>
                          Estimado: $0.00
                        </span>
                      )
                    ) : null}
                  </div>
                </div>
                <Button type="primary" danger htmlType="submit" loading={isProcessing} disabled={isProcessing} style={{ height: 32 }}>
                  Registrar y Procesar
                </Button>
              </div>
            </Form>
          )}
        </div>
      </div>
    </div>
  );
};

export default RequestValidationPage;
